/**
 * @file: The view model of the play report.
 *
 * The server reads the play log into one media list and four finding lists.
 * This class turns that result into the rows, the tiles, the finding lines and
 * the selected media that the template reads. It has no Angular dependency,
 * so a spec builds it with `new` and reads the model back.
 *
 * The server sends one entry per media, scene and festival day. The table
 * shows one row per media, so the grouping is here. The bar fraction, the
 * plays per hour and the scene columns all need that grouping. The class
 * builds it once per result and every query reads it.
 *
 * Every time of a finding is written in the zone of the window, and not in the
 * zone of the browser. The operator reads festival time, and a browser in
 * another zone must show the same line.
 *
 * The names and the colours of the scenes are not in the result. The class
 * takes them from the caller, because a deleted scene keeps its log rows and
 * must still show a name.
 */

import {
  mediaKey, asMs, startOf, festivalDay, zoneClock, zoneWeekday,
  dayOfKey, hourLabel, clockOfKey, dayLabel, hourKeysBetween
} from '../../../../shared/play-report'
import { groupBy } from '../../../../shared/group-by'
import { NEUTRAL_GREY } from 'common/colours'
import { formatCoarse } from 'common/clock'
import { compareNames, nameMatcher } from 'common/name-order'

const HOUR_MS = 3600000

/** A window longer than this counts by festival day and not by hour. */
const DAY_COLUMN_HOURS = 72

/** A finding line shows this many times. The rest are counted. */
const MAX_TIMES = 6

/** The chart shows about this many labels, whatever the window is. */
const AXIS_LABELS = 8

const UNENDED_NOTE = 'The player reported a start and nothing after it'

const QUIET_NOTE = 'The screen can still play from its cache'

/** The log holds a row with no name. The page needs a word to show for it. */
const NO_NAME = '(no name)'

/** The empty result, so every query works before the first load. */
const EMPTY_REPORT = Object.freeze({
  window: null,
  media: [],
  findings: Object.freeze({
    noContact: [],
    noContactTotal: 0,
    noContactLongestMs: 0,
    failed: [],
    failedTotal: 0,
    unended: [],
    unendedTotal: 0,
    silentNow: []
  })
})

/**
 * The two axes of the chart. A month has 720 hours, which is more columns than
 * the surface can draw, so a long window counts by day. Both axes read the
 * hour keys of the window, and the day axis folds them into days.
 */
const HOUR_AXIS = Object.freeze({
  heading: 'Plays per hour',
  bucketOf: hour => hour,
  labelOf: hourLabel
})

const DAY_AXIS = Object.freeze({
  heading: 'Plays per day',
  bucketOf: dayOfKey,
  labelOf: dayLabel
})

/** One scene of the open media, before any row is added to it. */
const EMPTY_TOTALS = Object.freeze({ plays: 0, screenMs: 0, failed: 0 })

const sum = values => values.reduce((total, value) => total + value, 0)

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

/** What the page shows for a name. The log can hold a row without one. */
const labelOfName = name => name || NO_NAME

/** The aggregate of one media, before any row is added to it. */
const emptyGroup = row => ({
  key: mediaKey(row),
  type: row.type,
  name: row.name,
  label: labelOfName(row.name),
  plays: 0,
  failed: 0,
  screenMs: 0,
  plannedMs: 0,
  playsByScene: {}
})

/** One server entry added to the aggregate of its media, as a new object. */
const addRow = (group, row) => ({
  ...group,
  plays: group.plays + row.plays,
  failed: group.failed + row.failed,
  screenMs: group.screenMs + row.screenMs,
  plannedMs: Math.max(group.plannedMs, row.plannedMs),
  playsByScene: {
    ...group.playsByScene,
    [row.sceneId]: (group.playsByScene[row.sceneId] || 0) + row.plays
  }
})

/** One aggregate per media of the window. */
const groupsOf = media => [...groupBy(media, mediaKey).values()]
  .map(rows => rows.reduce(addRow, emptyGroup(rows[0])))

/** The longest screen time first. Equal times keep the name order. */
const byScreenTime = (a, b) => (b.screenMs - a.screenMs) || (b.plays - a.plays) ||
  compareNames(a.name, b.name)

/**
 * One finding group of the side panel. The server caps some of the lists, so
 * the note says how many of the count reached the page. A list with no cap
 * sends every finding, and it needs no note.
 */
const findingGroup = ({ key, title, tone, total, shown = total, lines, kept }) => ({
  key,
  title,
  tone,
  count: total,
  note: shown < total ? `${kept} ${shown} shown` : '',
  lines
})

/** The plays of each scene in one column. A scene without plays has no segment. */
const segmentsOf = (scenes, rows) => scenes
  .map(scene => ({
    id: scene.id,
    name: scene.name,
    colour: scene.colour,
    plays: sum(rows.filter(row => Number(row.sceneId) === scene.id).map(row => row.plays))
  }))
  .filter(segment => segment.plays > 0)

class ReportState {
  constructor () {
    this._scenes = []
    this._report = EMPTY_REPORT
    this._timeZone = 'UTC'
    this._matchesSearch = nameMatcher('')
    this._groups = []
    this._sceneColumns = []
    this._selected = null
    this._hours = []
  }

  /** The scenes of the app, for the names, the colours and the dots. */
  setScenes (scenes) {
    this._scenes = [...(scenes || [])]
    this._sceneColumns = this._buildSceneColumns()
  }

  /**
   * The result of `playreport/getPlayReport`, with the zone that was sent with
   * it. The zone is an IANA name, and it decides both the festival day of a
   * row and the wall clock of every finding.
   *
   * The media rows are grouped here, once per result. Every query reads the
   * groups, and the table is built again on every keystroke of the search.
   *
   * A new window closes the open media. Its numbers came from the window
   * before, and the operator must not read them as the new one.
   */
  setReport (report, timeZone) {
    this._report = report || EMPTY_REPORT
    this._timeZone = timeZone || 'UTC'
    this._groups = groupsOf(this._report.media)
    this._sceneColumns = this._buildSceneColumns()
    this.close()
  }

  /** The text of the search box. It narrows the table and nothing else. */
  setSearch (text) {
    this._matchesSearch = nameMatcher(text)
  }

  /** Open one media in the right surface. Its hours arrive after this. */
  select (row) {
    this.close()
    this._selected = { type: row.type, name: row.name }
  }

  /** The result of `playreport/getPlayHours` for the open media. */
  setHours (hours) {
    this._hours = hours || []
  }

  /** Close the open media, so the findings come back. */
  close () {
    this._selected = null
    this._hours = []
  }

  /** The name of a scene. A deleted scene keeps its rows, so it keeps a name. */
  sceneName (sceneId) {
    const scene = this._sceneOf(sceneId)
    return scene ? scene.name : `Scene ${sceneId}`
  }

  /** The five tiles, read from every row of the window. */
  tiles () {
    const media = this._report.media
    // The tile shows the longest single period without contact. A sum over
    // the scenes can be longer than the window, which reads as a fault.
    const { noContactTotal, noContactLongestMs } = this._report.findings
    const sceneCount = new Set(media.map(row => row.sceneId)).size
    const hours = Math.round(this._windowHours())

    return {
      plays: sum(media.map(row => row.plays)),
      screenMs: sum(media.map(row => row.screenMs)),
      mediaCount: this._groups.length,
      longestNoContactMs: noContactLongestMs,
      noContactLabel: formatCoarse(noContactLongestMs),
      failed: sum(media.map(row => row.failed)),
      playsNote: `${plural(sceneCount, 'scene')}, ${plural(hours, 'hour')}`,
      noContactNote: `${plural(noContactTotal, 'period')}, see Findings`
    }
  }

  /** One column per scene that played in the window, in the order of the app. */
  sceneColumns () {
    return this._sceneColumns
  }

  /**
   * One row per media, longest screen time first.
   *
   * The bar is the screen time against the longest row that is shown, so a
   * search rescales the bars to what the operator reads.
   */
  rows () {
    const groups = this._groups.filter(group => this._matchesSearch(group))
    const longest = Math.max(0, ...groups.map(group => group.screenMs))
    const hours = this._windowHours()

    return groups
      .map(group => ({
        ...group,
        bar: longest > 0 ? (group.screenMs / longest) * 100 : 0,
        perHour: hours > 0 ? group.plays / hours : 0
      }))
      .sort(byScreenTime)
  }

  /** The four finding groups, with the empty ones left out. */
  findings () {
    const {
      noContact, noContactTotal, failed, failedTotal, unended, unendedTotal, silentNow
    } = this._report.findings
    const quiet = this._showsQuietNow() ? silentNow : []

    return {
      total: quiet.length + noContactTotal + failedTotal + unendedTotal,
      groups: [
        findingGroup({
          key: 'silentNow',
          title: 'Quiet now',
          tone: 'is-danger',
          total: quiet.length,
          lines: quiet.map(scene => this._quietLine(scene))
        }),
        findingGroup({
          key: 'noContact',
          title: 'No contact',
          tone: 'is-warning',
          total: noContactTotal,
          shown: noContact.length,
          kept: 'longest',
          lines: noContact.map(gap => this._gapLine(gap))
        }),
        findingGroup({
          key: 'failed',
          title: 'Failed plays',
          tone: 'is-danger',
          total: failedTotal,
          shown: failed.length,
          kept: 'newest',
          lines: this._playLines(failed, play => `Error: ${play.result || 'no message'}`)
        }),
        findingGroup({
          key: 'unended',
          title: 'Started, never ended',
          tone: '',
          total: unendedTotal,
          shown: unended.length,
          kept: 'newest',
          lines: this._playLines(unended, () => UNENDED_NOTE)
        })
      ].filter(group => group.count > 0)
    }
  }

  /** The open media, with its scenes and its hours. Null when none is open. */
  selected () {
    if (!this._selected) {
      return null
    }

    const key = mediaKey(this._selected)
    const rows = this._report.media.filter(row => mediaKey(row) === key)
    const hours = this._windowHours()
    const plays = sum(rows.map(row => row.plays))
    const scenes = this._sceneTotals(rows)

    return {
      key,
      type: this._selected.type,
      name: this._selected.name,
      label: labelOfName(this._selected.name),
      plays,
      screenMs: sum(rows.map(row => row.screenMs)),
      plannedMs: Math.max(0, ...rows.map(row => row.plannedMs)),
      perHour: hours > 0 ? plays / hours : 0,
      scenes,
      chart: this._chart(scenes)
    }
  }

  /** One CSV row per media, scene and festival day, in a stable order. */
  csvRows () {
    return this._report.media
      .map(row => ({
        day: row.day,
        scene: this.sceneName(row.sceneId),
        type: row.type,
        media: row.name,
        plays: row.plays,
        ended: row.ended,
        failed: row.failed,
        unended: row.unended,
        screenMs: row.screenMs
      }))
      .sort((a, b) => String(a.day).localeCompare(String(b.day)) ||
        compareNames(a.scene, b.scene) ||
        compareNames(a.media, b.media))
  }

  _sceneOf (sceneId) {
    return this._scenes.find(one => Number(one.id) === Number(sceneId))
  }

  _buildSceneColumns () {
    const present = new Set(this._report.media.map(row => Number(row.sceneId)))
    const known = this._scenes.filter(scene => present.has(Number(scene.id)))
    const knownIds = new Set(this._scenes.map(scene => Number(scene.id)))
    const unknown = [...present].filter(id => !knownIds.has(id)).sort((a, b) => a - b)

    return [...known.map(scene => this._column(scene.id)), ...unknown.map(id => this._column(id))]
  }

  _column (sceneId) {
    return { id: Number(sceneId), name: this.sceneName(sceneId), colour: this._colour(sceneId) }
  }

  _colour (sceneId) {
    const scene = this._sceneOf(sceneId)
    return (scene && scene.color) || NEUTRAL_GREY
  }

  /** The window as a number of hours. Zero before the first result. */
  _windowHours () {
    const window = this._report.window
    if (!window || !window.from || !window.to) {
      return 0
    }
    return Math.max(0, (asMs(window.to) - asMs(window.from)) / HOUR_MS)
  }

  /**
   * True when the heartbeat belongs to the window on the page. The heartbeat
   * tells about the present. A window that ended last week says nothing about
   * the screens of this hour.
   */
  _showsQuietNow () {
    const window = this._report.window
    return Boolean(window && window.to) && asMs(window.to) >= Date.now() - HOUR_MS
  }

  /** The wall clock of an instant in the zone of the window, as 'HH:mm'. */
  _clock (value) {
    return clockOfKey(zoneClock(value, this._timeZone))
  }

  /** A time with the weekday in front of it, such as 'Fri 20:03'. */
  _dayAndTime (value) {
    return `${zoneWeekday(value, this._timeZone)} ${this._clock(value)}`
  }

  _sameDay (a, b) {
    return festivalDay(a, this._timeZone) === festivalDay(b, this._timeZone)
  }

  /** A period. The second day is written only when the period crosses midnight. */
  _period (from, to) {
    return `${this._dayAndTime(from)} to ${this._sameDay(from, to) ? this._clock(to) : this._dayAndTime(to)}`
  }

  /**
   * The times of one finding line, oldest first. A time on the day of the time
   * before it drops the day, which is how a list of retries reads.
   */
  _timeList (times) {
    const sorted = [...times].sort((a, b) => asMs(a) - asMs(b))
    const shown = sorted.slice(0, MAX_TIMES)
      .map((time, index, all) => (
        index > 0 && this._sameDay(time, all[index - 1]) ? this._clock(time) : this._dayAndTime(time)
      ))
      .join(', ')

    return sorted.length > MAX_TIMES ? `${shown} and ${sorted.length - MAX_TIMES} more` : shown
  }

  _gapLine (gap) {
    return {
      colour: this._colour(gap.sceneId),
      title: this.sceneName(gap.sceneId),
      detail: `${this._period(gap.from, gap.to)} · ${formatCoarse(gap.ms)}`,
      note: `Last report before the gap: ${gap.lastName || 'none'}`
    }
  }

  /** One line per scene that is out of contact now, from the heartbeat. */
  _quietLine ({ sceneId, since }) {
    return {
      colour: this._colour(sceneId),
      title: this.sceneName(sceneId),
      detail: `no heartbeat since ${this._dayAndTime(since)}`,
      note: QUIET_NOTE
    }
  }

  /**
   * One line per media, scene and note. Three retries of the same clip are one
   * line with three times, because three lines say the same thing.
   */
  _playLines (plays, noteOf) {
    const groups = groupBy(plays, play => JSON.stringify([play.sceneId, mediaKey(play), noteOf(play)]))

    return [...groups.values()].map(own => ({
      colour: this._colour(own[0].sceneId),
      title: labelOfName(own[0].name),
      detail: `${this.sceneName(own[0].sceneId)} · ${this._timeList(own.map(startOf))}`,
      note: noteOf(own[0])
    }))
  }

  /**
   * The scenes the open media played on, in the order of the columns. A scene
   * without a play of this media is left out, so the table and the legend of
   * the chart name the same scenes.
   */
  _sceneTotals (rows) {
    const totals = rows.reduce((byScene, row) => {
      const id = Number(row.sceneId)
      const own = byScene.get(id) || EMPTY_TOTALS
      return byScene.set(id, {
        plays: own.plays + row.plays,
        screenMs: own.screenMs + row.screenMs,
        failed: own.failed + row.failed
      })
    }, new Map())

    return this._sceneColumns
      .map(column => ({ ...column, ...(totals.get(column.id) || EMPTY_TOTALS) }))
      .filter(scene => scene.plays > 0)
  }

  /**
   * The stacked columns of the open media, one column per hour or per day of
   * the window.
   *
   * The server sends only the keys with plays. An empty column shows when the
   * media stopped, so the whole window is walked here. The columns are built
   * once, and the top, the half and the legend are read from them.
   */
  _chart (scenes) {
    const axis = this._windowHours() > DAY_COLUMN_HOURS ? DAY_AXIS : HOUR_AXIS
    const byKey = groupBy(this._hours, row => axis.bucketOf(row.hour))
    const keys = this._axisKeys(axis)
    const step = Math.max(1, Math.ceil(keys.length / AXIS_LABELS))
    const columns = keys.map((key, index) => {
      const segments = segmentsOf(scenes, byKey.get(key) || [])
      return {
        key,
        label: index % step === 0 ? axis.labelOf(key) : null,
        total: sum(segments.map(segment => segment.plays)),
        segments
      }
    })
    const top = Math.max(1, ...columns.map(column => column.total))
    const played = new Set(columns.flatMap(column => column.segments.map(segment => segment.id)))

    return {
      heading: axis.heading,
      top,
      half: Math.round(top / 2),
      legend: scenes.filter(scene => played.has(scene.id)),
      columns: columns.map(column => ({
        ...column,
        segments: column.segments.map(segment => ({ ...segment, percent: (segment.plays / top) * 100 }))
      }))
    }
  }

  /** One key per column, from the hours of the window. */
  _axisKeys (axis) {
    const window = this._report.window
    if (!window || !window.from || !window.to) {
      return []
    }

    const hours = hourKeysBetween(window.from, window.to, this._timeZone)

    return [...new Set(hours.map(key => axis.bucketOf(key)))]
  }
}

export { ReportState }
